import type { ProceduralProfile } from "./AudioProfiles.ts";
import { synthesizeProceduralSamples } from "./ProceduralSynth.ts";
import {
  MUSIC_PAD_GAIN,
  MUSIC_BASS_GAIN,
  MUSIC_PERC_KICK_GAIN,
  MUSIC_PERC_HAT_GAIN,
  MUSIC_ARP_GAIN,
  MUSIC_TENSION_GAIN,
  MUSIC_LEAD_GAIN,
} from "./AudioLoudness.ts";

/**
 * MusicPatterns.ts — Task-001 (2026-ingame-music-rework, AC-3/AC-6). Pure
 * data + pure functions for the `"run"` track's procedural composer
 * (`ProceduralMusic.ts`). NO Web Audio imports (headless-testable by
 * construction, no `AudioContext`/`AudioBuffer` anywhere below) and NO
 * `gs.rng` (render-side only, per `docs/AUDIO_DIRECTION.md:9` — "Audio may use
 * Math.random freely, it is not sim RNG"): every draw here goes through the
 * small `mulberry32`-style local PRNG below, seeded from the caller's
 * arguments, never the shared sim stream.
 *
 * Musical direction (owner, 2026-07-20, spec Assumption B-9): **dark
 * pulse-ambient** — a dark, calmly pulsing bed that densifies with wave
 * intensity / near-loss pressure (the existing "calm ambient, keep beats"
 * canon, D-233/D-240). Every constant below is named + a single-place edit
 * (the D-244 "one-line ear-check tune" pattern) — this file IS the surface
 * the spec's one allowed revision round (AC-8) tunes; the composer's
 * ARCHITECTURE is deliberately indifferent to these values (tech-analysis
 * §2.2).
 *
 * Determinism contract: `generateBarPattern(seed, energy, barIndex)` and
 * `renderBarSamples(seed, energy, bars)` are PURE functions of their
 * arguments — no shared/mutable module state, no closure over a live PRNG
 * object — so calling either twice with identical arguments returns
 * deep-equal (byte-identical, respectively) results. That is what the
 * pattern-determinism witness (`tests/systems/ProceduralMusic.test.ts`)
 * exercises directly.
 */

// ── Tempo (owner-tunable) ────────────────────────────────────────────────

/** The composer's base tempo — a calm pulse, deliberately under the ~104 BPM
 *  "feel" ceiling from the tech-analysis so the bed never rushes. */
export const BASE_BPM = 100;

/** 16th-note grid, 4/4 — the scheduler's atomic step unit for every layer. */
export const STEPS_PER_BAR = 16;

/** Composer tempo-scale clamp — mirrors `AudioEngine.setMusicRate`'s ±6%
 *  clamp exactly, so a scene rate nudge (boss/defeat presets) reads
 *  identically regardless of which source (buffer loop or composer) is live. */
export const TEMPO_SCALE_MIN = 0.94;
export const TEMPO_SCALE_MAX = 1.06;

export function clampTempoScale(scale: number): number {
  return Math.max(TEMPO_SCALE_MIN, Math.min(TEMPO_SCALE_MAX, scale));
}

/** Seconds per 16th-note step at `bpm` (4 steps/beat, 4/4). */
export function secondsPerStep(bpm: number = BASE_BPM): number {
  return 60 / bpm / 4;
}

/** Seconds per bar (`STEPS_PER_BAR` steps) at `bpm`. */
export function barDurationSec(bpm: number = BASE_BPM): number {
  return secondsPerStep(bpm) * STEPS_PER_BAR;
}

// ── Scale — "dark dorian" (owner direction: dark, not a flat dirge) ────────

/** E2 — a dark, low anchor (MIDI 40 → 82.4069 Hz). */
export const ROOT_MIDI = 40;

/** Dorian-mode semitone offsets from the root: the minor 3rd + minor 7th
 *  read as "dark"; the natural (major) 6th keeps it from reading as a flat
 *  drone-doom dirge — the "pulse-ambient" half of the direction. */
export const SCALE_INTERVALS = [0, 2, 3, 5, 7, 9, 10] as const;

export function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Scale-degree (0-based, wraps octaves upward; negative degrees wrap down
 *  correctly) + an octave offset (0 = the root's own octave) → Hz. */
export function scaleFreq(degree: number, octaveOffset = 0): number {
  const len = SCALE_INTERVALS.length;
  const wrapped = ((degree % len) + len) % len;
  const octave = Math.floor(degree / len) + octaveOffset;
  return midiToFreq(ROOT_MIDI + SCALE_INTERVALS[wrapped]! + 12 * octave);
}

// ── Layers + the energy gate (the "densifies with intensity" shape) ────────

export type MusicLayer =
  | "pad"
  | "bass"
  | "percussion"
  | "arp"
  | "tension"
  | "lead";

export const MUSIC_LAYERS: readonly MusicLayer[] = [
  "pad",
  "bass",
  "percussion",
  "arp",
  "tension",
  // Task-003 (2026-audio-polish, AC-6) — appended, not interleaved: every
  // existing layer keeps its exact construction-order index (gain-node
  // creation order, `ProceduralMusic`'s constructor loop), so this is a
  // purely additive change for every pre-existing name-indexed test/witness.
  "lead",
];

/** Layers PerfGovernor may shed under sustained load (§2.6) — arp first
 *  conceptually (the densest, most expendable motion), percussion with it;
 *  both shed together at the same threshold below. Pad/bass/tension never
 *  shed: they're what keeps a shed bar still recognizably "the run's music"
 *  instead of collapsing toward silence. */
export const SHEDDABLE_LAYERS: ReadonlySet<MusicLayer> = new Set([
  "arp",
  "percussion",
]);

/** PerfGovernor level (0..6, `PerfGovernor.ts:170-171`) at/above which
 *  `SHEDDABLE_LAYERS` stop scheduling new notes — the level where the
 *  visible post-FX cuts already start (`POSTFX_BY_LEVEL`,
 *  `PerfGovernor.ts:52-60`): the D-240 density-throttle philosophy applied
 *  to music. */
export const GOVERNOR_SHED_LEVEL = 4;

export interface LayerEnergyConfig {
  /** Energy at/below which the layer is fully silent. */
  readonly threshold: number;
  /** Energy at/above which the layer is at its full gain multiplier (1). A
   *  soft-knee linear ramp fills the gap between — layers "densify", they
   *  don't snap in. */
  readonly full: number;
}

/** Per-layer energy gate — owner-tunable; this table alone is the
 *  "densifies with wave intensity" shape the musical direction asks for. */
export const LAYER_ENERGY: Readonly<Record<MusicLayer, LayerEnergyConfig>> = {
  pad: { threshold: 0, full: 0.2 }, // L0 — always on, the calm floor
  bass: { threshold: 0.1, full: 0.4 }, // L1 — the pulse joins early
  // Task-003 (2026-audio-polish, AC-6) — the melody: fading up gently via the
  // same soft-knee gate every other layer uses. REVISION (2026-07-22, Build 67
  // ear-check): threshold 0.25 → 0.12 so the melody is present from EARLY /
  // calmer combat too — at 0.25 the quieter stretches carried no lead at all,
  // a big part of why the run read as "absolut monoton". `full` stays 0.6 so
  // it still swells with intensity rather than sitting pinned at max.
  lead: { threshold: 0.12, full: 0.6 },
  percussion: { threshold: 0.35, full: 0.65 }, // L2 — soft beats, moderate energy
  arp: { threshold: 0.55, full: 0.85 }, // L3 — mid-register motion
  tension: { threshold: 0.75, full: 1.0 }, // L4 — boss / near-loss only
};

/** The layer's gain multiplier (0..1) at a given energy — a soft-knee ramp
 *  from `threshold` (0) to `full` (1), flat at 1 above `full`.
 *  `ProceduralMusic.setEnergy` ramps each layer's GainNode to this value and
 *  `renderBarSamples` applies the SAME multiplier to the pure-math mix, so
 *  the loudness witness measures exactly what the runtime would play. */
export function layerGainForEnergy(layer: MusicLayer, energy: number): number {
  const { threshold, full } = LAYER_ENERGY[layer];
  const e = Math.max(0, Math.min(1, energy));
  if (e <= threshold) return 0;
  if (e >= full) return 1;
  return (e - threshold) / (full - threshold);
}

/** Default energy before any `setMusicEnergy` call ever lands (the
 *  tech-analysis's "combat" scene preset baseline, §2.4) — so a composer
 *  started with no energy wiring yet (Task-002's job) still plays a sensible
 *  bed instead of just the bare pad. */
export const DEFAULT_ENERGY = 0.45;

// ── Voices — recipes reusing the existing synthesis shapes (ProceduralProfile) ──

export type MusicVoiceId =
  | "pad"
  | "bass"
  | "percKick"
  | "percHat"
  | "arp"
  | "tension"
  | "lead";

/** Explicit iteration order (avoids an `Object.keys(...) as MusicVoiceId[]`
 *  cast at every call site — `ProceduralMusic` pre-synthesizes one buffer per
 *  entry at construction). Task-003 (2026-audio-polish, AC-6) — `lead` is
 *  APPENDED, not interleaved, so every pre-existing name-indexed
 *  buffer/gain-node lookup (`MUSIC_VOICE_IDS.indexOf(...)`) stays unaffected. */
export const MUSIC_VOICE_IDS: readonly MusicVoiceId[] = [
  "pad",
  "bass",
  "percKick",
  "percHat",
  "arp",
  "tension",
  "lead",
];

export const VOICE_LAYER: Readonly<Record<MusicVoiceId, MusicLayer>> = {
  pad: "pad",
  bass: "bass",
  percKick: "percussion",
  percHat: "percussion",
  arp: "arp",
  tension: "tension",
  lead: "lead",
};

/** A voice's synthesis recipe (the exact `ProceduralProfile` shape the
 *  existing SFX machinery already understands — reused verbatim, never
 *  reinvented) at its REFERENCE pitch, plus that reference frequency so the
 *  runtime can pitch-shift ONE pre-rendered buffer via
 *  `AudioBufferSourceNode.playbackRate` instead of re-synthesizing per note
 *  (D-298: voice buffers stay small — a handful total, not one per note). */
export interface MusicVoiceDef {
  readonly recipe: ProceduralProfile;
  /** Reference pitch the recipe was rendered at (Hz); ignored when
   *  `pitched` is false. */
  readonly referenceFreqHz: number;
  /** Whether per-note events carry a target `freqHz` this voice should
   *  pitch-shift to (bass/arp); false for voices that always play their
   *  baked reference verbatim (pad's chord, percussion, the tension drone). */
  readonly pitched: boolean;
  /** F-1 (perf-review finding, Task-001 attempt 2): optional per-voice
   *  override for the buffer's own render sample rate, passed straight
   *  through to `synthesizeProceduralBuffer` — omitted ⇒ render at the
   *  AudioContext's own rate (every voice except the two below). Only
   *  `pad`/`tension` set it (see `LONG_VOICE_RENDER_SAMPLE_RATE_HZ`'s doc). */
  readonly renderSampleRateHz?: number;
}

/** F-1 fix — the two LONG voices (`pad`, `tension`) were measured retaining
 *  1.302 MiB (1,365,336 B) of the composer's ~1.40 MiB total buffer memory at a 44.1 kHz
 *  ctx rate, ~30× the "dozens of KB" this file's/`ProceduralMusic.ts`'s
 *  original comments claimed (D-298; allocated inside the WKWebView-jetsam-
 *  sensitive boot window, `main.ts:1119`). Both voices' CONTENT is pure low
 *  sine energy — pad: 3 sines ≤ 247 Hz (`PAD_CHORD_DEGREES` at
 *  `PAD_OCTAVE_OFFSET`); tension: one sine ≈ 349 Hz (`TENSION_FREQ_HZ`) —
 *  nothing above ~350 Hz, so rendering them at a much lower rate is audibly
 *  transparent: Nyquist at 11,025 Hz is ≈5.5 kHz, comfortably above their
 *  content and still well over the Web Audio spec's 8,000 Hz buffer-rate
 *  floor. `AudioBufferSourceNode` resamples any buffer to the destination
 *  context's rate automatically on playback, and `playbackRate` is already
 *  relative to the buffer's OWN sample rate (`ProceduralMusic.playNote`) —
 *  so this is a pure memory win with no runtime pitch/tempo compensation
 *  needed anywhere. NOISE-based voices (kick, hat) deliberately do NOT use
 *  this — broadband noise content actually needs the ctx rate's full
 *  bandwidth. See `ProceduralMusic.ts`'s buffer-count comment for the
 *  measured before/after totals. */
export const LONG_VOICE_RENDER_SAMPLE_RATE_HZ = 11_025;

const PAD_OCTAVE_OFFSET = 1;
/** Root, 3rd, 5th within the dorian scale — a minor triad (0,3,7 semitones). */
const PAD_CHORD_DEGREES = [0, 2, 4] as const;
/** Pad retriggers every N bars — a slow-moving wash, not a busy re-attack. */
export const PAD_BAR_PERIOD = 2;
/** Longer than `PAD_BAR_PERIOD` so consecutive pad notes overlap into a
 *  seamless cross-fade instead of a gap or a hard re-trigger click. */
const PAD_DURATION_BARS = PAD_BAR_PERIOD + 0.6;

const BASS_OCTAVE_OFFSET = 0;

/** Reserved >2 kHz band (AUDIO_BIBLE diegetic rule) — the arp's octave stays
 *  well under it; see `arpMaxFreqHz()` below for the closed-form proof. */
const ARP_OCTAVE_OFFSET = 3;
const ARP_DEGREE_RANGE = 8;

const TENSION_OCTAVE_OFFSET = 2;
/** A minor 2nd ABOVE the root — deliberately outside the scale (dissonant
 *  against the pad's tonic; "pressure becomes musical", tech-analysis §2.5). */
const TENSION_FREQ_HZ = midiToFreq(ROOT_MIDI + 1 + 12 * TENSION_OCTAVE_OFFSET);

/** Task-003 (2026-audio-polish, AC-6) — the restrained lead/melody voice.
 *  E4 register (two octaves above the E2 root — `scaleFreq(0,
 *  LEAD_OCTAVE_OFFSET)` ≈ 329.6 Hz): high enough to read as a melody over
 *  the pad bed, low enough to stay well clear of the reserved >2 kHz SFX
 *  band even at its worst-case degree (see `leadMaxFreqHz()`). */
const LEAD_OCTAVE_OFFSET = 2;
/** Long and breathy — NOT the arp's 160 ms plink (tech-analysis §3.1). */
const LEAD_DURATION_MS = 800;
/** Closed-form register ceiling: the highest scale degree the phrase-motif
 *  walk (`generatePhraseMotif`) can ever reach (its per-step clamp enforces
 *  this — see that function). `leadMaxFreqHz()` below turns this into a Hz
 *  bound; at `LEAD_OCTAVE_OFFSET`, degree `LEAD_DEGREE_RANGE - 1` (= 9) is
 *  ≈ 784 Hz (G5) — comfortably under the reserved >2 kHz band. */
const LEAD_DEGREE_RANGE = 10;

export const VOICE_DEFS: Readonly<Record<MusicVoiceId, MusicVoiceDef>> = {
  pad: {
    recipe: {
      type: "chord",
      durationMs: barDurationSec() * PAD_DURATION_BARS * 1000,
      freqs: PAD_CHORD_DEGREES.map((d) => scaleFreq(d, PAD_OCTAVE_OFFSET)),
      gain: MUSIC_PAD_GAIN,
      decay: "linear",
    },
    referenceFreqHz: scaleFreq(0, PAD_OCTAVE_OFFSET),
    pitched: false,
    renderSampleRateHz: LONG_VOICE_RENDER_SAMPLE_RATE_HZ,
  },
  bass: {
    recipe: {
      type: "triangle",
      durationMs: 220,
      freq: scaleFreq(0, BASS_OCTAVE_OFFSET),
      gain: MUSIC_BASS_GAIN,
      decay: "exp",
    },
    referenceFreqHz: scaleFreq(0, BASS_OCTAVE_OFFSET),
    pitched: true,
  },
  percKick: {
    recipe: {
      type: "noise",
      durationMs: 140,
      gain: MUSIC_PERC_KICK_GAIN,
      decay: "exp",
      lowpass: 160,
    },
    referenceFreqHz: 1,
    pitched: false,
  },
  percHat: {
    recipe: {
      type: "noise",
      durationMs: 45,
      gain: MUSIC_PERC_HAT_GAIN,
      decay: "exp",
    },
    referenceFreqHz: 1,
    pitched: false,
  },
  arp: {
    recipe: {
      type: "sine",
      durationMs: 160,
      freq: scaleFreq(0, ARP_OCTAVE_OFFSET),
      gain: MUSIC_ARP_GAIN,
      decay: "exp",
    },
    referenceFreqHz: scaleFreq(0, ARP_OCTAVE_OFFSET),
    pitched: true,
  },
  tension: {
    recipe: {
      type: "sine",
      durationMs: 1500,
      freq: TENSION_FREQ_HZ,
      gain: MUSIC_TENSION_GAIN,
      decay: "linear",
    },
    referenceFreqHz: TENSION_FREQ_HZ,
    pitched: false,
    renderSampleRateHz: LONG_VOICE_RENDER_SAMPLE_RATE_HZ,
  },
  // Task-003 (2026-audio-polish, AC-6) — the melody: a pure sine (no
  // harmonics, kindest to the ear sitting over the pad bed), pitched (each
  // note plays a phrase-motif degree via pitch-shifted playback, exactly
  // like bass/arp), rendered at `LONG_VOICE_RENDER_SAMPLE_RATE_HZ` (the F-1
  // pattern — content is a ≤ ~800 Hz sine, far under that rate's ≈5.5 kHz
  // Nyquist).
  lead: {
    recipe: {
      type: "sine",
      durationMs: LEAD_DURATION_MS,
      freq: scaleFreq(0, LEAD_OCTAVE_OFFSET),
      gain: MUSIC_LEAD_GAIN,
      decay: "linear",
    },
    referenceFreqHz: scaleFreq(0, LEAD_OCTAVE_OFFSET),
    pitched: true,
    renderSampleRateHz: LONG_VOICE_RENDER_SAMPLE_RATE_HZ,
  },
};

/** Worst-case (highest) arp fundamental — both the composer's own register
 *  invariant and its test witness use this closed form. Must stay below the
 *  reserved >2 kHz SFX band (`AUDIO_BIBLE.md`'s diegetic rule; the existing
 *  2 kHz brightness shelf, `AudioEngine.ts:521-524`). */
export function arpMaxFreqHz(): number {
  return scaleFreq(ARP_DEGREE_RANGE - 1, ARP_OCTAVE_OFFSET);
}

/** Worst-case (highest) lead fundamental — the closed-form witness AC-6 asks
 *  for, mirroring `arpMaxFreqHz()`. `generatePhraseMotif`'s per-step clamp
 *  guarantees no motif degree ever exceeds `LEAD_DEGREE_RANGE - 1`, so this
 *  is a real upper bound, not a statistical one. Must stay below the
 *  reserved >2 kHz SFX band. */
export function leadMaxFreqHz(): number {
  return scaleFreq(LEAD_DEGREE_RANGE - 1, LEAD_OCTAVE_OFFSET);
}

// ── Seeded pattern generation (fixed seed + energy ⇒ deterministic events) ──

export interface MusicNoteEvent {
  readonly layer: MusicLayer;
  readonly voice: MusicVoiceId;
  /** 0..STEPS_PER_BAR-1 — the 16th-note step this note fires on. */
  readonly stepIndex: number;
  /** Target frequency (Hz) for pitched voices; the voice's own reference
   *  pitch for unpitched ones (see `MusicVoiceDef.pitched`). */
  readonly freqHz: number;
  /** 0..~1.08 (macro pad velocity may exceed 1; loudness witnesses gate the
   *  result) per-note gain multiplier (seeded humanization). */
  readonly velocity: number;
}

const BASS_QUARTER_STEPS = [0, 4, 8, 12] as const;
const BASS_EIGHTH_STEPS = [2, 6, 10, 14] as const;
/** Above this layer-gain, the driving eighth-note bass pulse joins the
 *  steady quarters (denser feel as energy nears the layer's `full`). */
const BASS_DENSE_GAIN = 0.7;

const PERC_KICK_STEPS: readonly number[] = [0, 8];
const PERC_HAT_MIN = 2;
const PERC_HAT_MAX = 10;

const ARP_EIGHTH_STEPS = [0, 2, 4, 6, 8, 10, 12, 14] as const;
const ARP_NOTES_MIN = 2;
const ARP_NOTES_MAX = 8;

const HUMANIZE_MIN = 0.8;
const HUMANIZE_MAX = 1.0;

const ALL_STEPS: readonly number[] = Array.from(
  { length: STEPS_PER_BAR },
  (_, i) => i,
);

/** mulberry32 — a small, fast, well-distributed 32-bit PRNG. Render-side
 *  only (never `gs.rng`); every call site below either seeds a fresh
 *  instance from arguments (pure) or the composer's own constructor seed
 *  (`ProceduralMusic.ts`), matching `AUDIO_DIRECTION.md:9`. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Derive a deterministic per-bar sub-seed from the composer's seed + the
 *  bar index, so a bar's fill/degree-walk draws are reproducible from
 *  `(seed, barIndex)` alone — no shared/mutable RNG object to thread
 *  through, which is what keeps `generateBarPattern` a pure function. */
function barSeed(seed: number, barIndex: number): number {
  return (seed ^ Math.imul(barIndex + 1, 0x9e3779b9)) >>> 0;
}

function humanize(rng: () => number): number {
  return HUMANIZE_MIN + (HUMANIZE_MAX - HUMANIZE_MIN) * rng();
}

/** Fisher–Yates partial shuffle → the first `n` picks, returned in ascending
 *  step order (a stable, easy-to-schedule/test event list). Pure given `rng`. */
function pickN(pool: readonly number[], n: number, rng: () => number): number[] {
  const count = Math.max(0, Math.min(n, pool.length));
  const arr = [...pool];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr.slice(0, count).sort((a, b) => a - b);
}

// ── Phrase motifs (Task-003, 2026-audio-polish, AC-6) — the seeded melody ──

/** A phrase (the lead's melodic unit) spans this many bars — `phraseIndex =
 *  ⌊barIndex / PHRASE_BARS⌋` (≈ 19.2 s at `BASE_BPM`'s 2.4 s bar). */
export const PHRASE_BARS = 8;

const LEAD_MOTIF_MIN_NOTES = 3;
const LEAD_MOTIF_MAX_NOTES = 5;
/** Stepwise walk: consecutive motif notes never leap more than this many
 *  scale degrees — "unaufgeregt" (calm) as a rule, not a scale run. */
const LEAD_STEP_MAX = 2;
/** The motif's FIRST degree is drawn from `[0, LEAD_START_DEGREE_RANGE)` —
 *  low in the register, matching "small range". */
const LEAD_START_DEGREE_RANGE = 3;

/** Derive a deterministic per-phrase sub-seed from the composer's seed + the
 *  phrase index — the phrase-motif twin of `barSeed` below (a different
 *  multiplier decorrelates the two derived seed streams). */
function phraseSeed(seed: number, phraseIndex: number): number {
  return (seed ^ Math.imul(phraseIndex + 1, 0x27d4eb2f)) >>> 0;
}

/**
 * A phrase's motif: 3–5 scale degrees, a stepwise dark-dorian walk with a
 * small range — PURE `f(seed, phraseIndex)` (tech-analysis §3.2). Every
 * degree is clamped to `[0, LEAD_DEGREE_RANGE)`, which is what makes
 * `leadMaxFreqHz()` a real closed-form bound rather than a statistical one.
 * Same phrase ⇒ same motif (phrase-stable); a different `phraseIndex` (near-
 * certainly) draws a different motif. Placement into bars (which 1–2 notes
 * sound on a given bar, and whether the bar plays at all — the "occasional
 * rest") is `generateBarPattern`'s job, below — this function only supplies
 * the pitch material.
 */
export function generatePhraseMotif(
  seed: number,
  phraseIndex: number,
): readonly number[] {
  const rng = mulberry32(phraseSeed(seed, phraseIndex));
  const noteCount =
    LEAD_MOTIF_MIN_NOTES +
    Math.floor(rng() * (LEAD_MOTIF_MAX_NOTES - LEAD_MOTIF_MIN_NOTES + 1));
  const degrees: number[] = [Math.floor(rng() * LEAD_START_DEGREE_RANGE)];
  for (let i = 1; i < noteCount; i++) {
    const step = 1 + Math.floor(rng() * LEAD_STEP_MAX);
    const dir = rng() < 0.5 ? -1 : 1;
    const next = degrees[i - 1]! + dir * step;
    degrees.push(Math.max(0, Math.min(LEAD_DEGREE_RANGE - 1, next)));
  }
  return degrees;
}

// ── Macro drift ("leicht dahinfaden", Task-003, 2026-audio-polish, AC-7) ───
// A slow (minutes-scale), PURELY barIndex-driven envelope (never energy —
// Assumption P-9) so a long run breathes even at constant energy, without
// touching the scheduler (generation-side only, zero new timers). A
// raised-cosine curve — smooth, no discontinuity at the period seam — maps
// onto exactly the four documented dimensions below; energy and macro
// MULTIPLY (each dimension's macro nudge sits on top of the existing
// energy-driven baseline), they don't fight (tech-analysis §3.3).

/** ≈ 115 s at `BASE_BPM` (48 bars × 2.4 s/bar) — "minutes-scale ebb and flow". */
export const MACRO_PERIOD_BARS = 48;

/** The macro curve itself: 0..1, smooth, periodic every `MACRO_PERIOD_BARS`
 *  bars. 0.5 is the curve's own midpoint; every dimension below maps
 *  `2·macroPhase(barIndex) - 1` (a signed -1..1 swing) through its own depth. */
export function macroPhase(barIndex: number): number {
  const period = MACRO_PERIOD_BARS;
  const t = ((barIndex % period) + period) % period;
  return 0.5 - 0.5 * Math.cos((2 * Math.PI * t) / period);
}

/** Pad-velocity depth: ± this many gain-multiplier units around 1. Small —
 *  `pad` anchors `MUSIC_BED` (`AudioLoudness.ts`), so it breathes, it does
 *  not visibly pump. */
export const MACRO_PAD_VEL_DEPTH = 0.08;
/** The pad's per-bar velocity multiplier (replaces a flat `1`). */
export function macroPadVelocity(barIndex: number): number {
  return 1 + MACRO_PAD_VEL_DEPTH * (2 * macroPhase(barIndex) - 1);
}

/** Hat-fill depth: ± this many additional/fewer hats — a small integer
 *  swing so the percussion texture ebbs without ever reading as a glitch. */
export const MACRO_HAT_FILL_DEPTH = 2;
export function macroHatFillDelta(barIndex: number): number {
  return Math.round(MACRO_HAT_FILL_DEPTH * (2 * macroPhase(barIndex) - 1));
}

/** Lead-presence depth: ± this many probability units, added to
 *  `LEAD_BASE_PRESENCE` (below) then clamped 0..1 — the macro's own
 *  contribution to how often an energy-eligible bar actually plays a motif
 *  note. */
export const MACRO_LEAD_PRESENCE_DEPTH = 0.25;
export function macroLeadPresenceDelta(barIndex: number): number {
  return MACRO_LEAD_PRESENCE_DEPTH * (2 * macroPhase(barIndex) - 1);
}

/** Bass eighth-join depth: ± this many gain units around `BASS_DENSE_GAIN` —
 *  the "bass eighth-join nudge": the driving eighth-note pulse joins a
 *  little earlier in the energy ramp at the macro's peak, a little later at
 *  its trough. */
export const MACRO_BASS_JOIN_DEPTH = 0.1;
export function macroBassJoinThreshold(barIndex: number): number {
  return (
    BASS_DENSE_GAIN - MACRO_BASS_JOIN_DEPTH * (2 * macroPhase(barIndex) - 1)
  );
}

/** Baseline probability an energy-eligible bar actually plays a lead motif
 *  note (before the macro nudge) — keeps the melody "restrained"/sparse
 *  rather than on every single bar (the "occasional rest"). */
const LEAD_BASE_PRESENCE = 0.5;
/** Half/whole-note slots (mirrors `PERC_KICK_STEPS`'s shape) — the lead
 *  never plays off-grid. */
const LEAD_SLOT_STEPS: readonly number[] = [0, 8];
/** Above this (soft-knee) lead gain, 2 motif notes place instead of 1 —
 *  mirrors `BASS_DENSE_GAIN`'s role for the bass layer. */
const LEAD_DENSE_GAIN = 0.7;

/**
 * One bar's worth of deterministic note events for `(seed, energy, barIndex)`
 * — a PURE function (see the module doc's determinism contract). Layer
 * AUDIBILITY is a pure gain fact (`layerGainForEnergy`); layer PRESENCE in
 * the returned events mirrors it 1:1 (a layer at gain 0 contributes NO
 * events, not just silent ones) — so "percussion silent below its energy
 * threshold" is witnessable both ways (no events, and — via
 * `renderBarSamples` — no audible contribution).
 */
export function generateBarPattern(
  seed: number,
  energy: number,
  barIndex: number,
): readonly MusicNoteEvent[] {
  const e = Math.max(0, Math.min(1, energy));
  const rng = mulberry32(barSeed(seed, barIndex));
  const events: MusicNoteEvent[] = [];

  // L0 — pad/drone bed: a slow-moving chord wash, retriggered every
  // PAD_BAR_PERIOD bars. Always in-pattern (its own gate is threshold 0 —
  // "always on, the calm floor"). Task-003 (AC-7) — velocity now carries the
  // macro drift's pad-velocity nudge (± MACRO_PAD_VEL_DEPTH around 1,
  // `macroPadVelocity`) instead of a flat 1 — the bed breathes slowly.
  if (barIndex % PAD_BAR_PERIOD === 0) {
    events.push({
      layer: "pad",
      voice: "pad",
      stepIndex: 0,
      freqHz: VOICE_DEFS.pad.referenceFreqHz,
      velocity: macroPadVelocity(barIndex),
    });
  }

  // L1 — bass pulse: steady quarters; an extra eighth-note pulse joins once
  // the layer nears full gain (a driving, denser feel at high energy).
  // Task-003 (AC-7) — the join THRESHOLD itself carries the macro drift's
  // "bass eighth-join nudge" (`macroBassJoinThreshold`, ± MACRO_BASS_JOIN_DEPTH
  // around BASS_DENSE_GAIN) so the join point drifts a little earlier/later
  // in the energy ramp over the macro period.
  const bassGain = layerGainForEnergy("bass", e);
  if (bassGain > 0) {
    for (const step of BASS_QUARTER_STEPS) {
      events.push({
        layer: "bass",
        voice: "bass",
        stepIndex: step,
        freqHz: VOICE_DEFS.bass.referenceFreqHz,
        velocity: humanize(rng),
      });
    }
    if (bassGain >= macroBassJoinThreshold(barIndex)) {
      for (const step of BASS_EIGHTH_STEPS) {
        events.push({
          layer: "bass",
          voice: "bass",
          stepIndex: step,
          freqHz: VOICE_DEFS.bass.referenceFreqHz,
          velocity: humanize(rng),
        });
      }
    }
  }

  // L2 — soft percussion: a sparse kick anchor + seeded hat fills whose
  // COUNT scales with the layer's gain (the "densifies" story). Task-003
  // (AC-7) — the count additionally carries the macro drift's hat-fill nudge
  // (`macroHatFillDelta`, ± MACRO_HAT_FILL_DEPTH), clamped to the available
  // slot range so it never asks for a negative or over-full count.
  const percGain = layerGainForEnergy("percussion", e);
  if (percGain > 0) {
    for (const step of PERC_KICK_STEPS) {
      events.push({
        layer: "percussion",
        voice: "percKick",
        stepIndex: step,
        freqHz: VOICE_DEFS.percKick.referenceFreqHz,
        velocity: humanize(rng),
      });
    }
    const hatSlots = ALL_STEPS.filter((s) => !PERC_KICK_STEPS.includes(s));
    const hatCount = Math.max(
      0,
      Math.min(
        hatSlots.length,
        Math.round(PERC_HAT_MIN + (PERC_HAT_MAX - PERC_HAT_MIN) * percGain) +
          macroHatFillDelta(barIndex),
      ),
    );
    for (const step of pickN(hatSlots, hatCount, rng)) {
      events.push({
        layer: "percussion",
        voice: "percHat",
        stepIndex: step,
        freqHz: VOICE_DEFS.percHat.referenceFreqHz,
        velocity: humanize(rng),
      });
    }
  }

  // L3 — arp (mid-register motion): a seeded scale-degree pick on a subset
  // of eighth-note slots — below the reserved >2 kHz SFX band (see
  // `arpMaxFreqHz()`). The FILLED slot count scales with the layer's gain.
  // (The dedicated melodic voice is `lead`, below — arp stays motion/texture.)
  const arpGain = layerGainForEnergy("arp", e);
  if (arpGain > 0) {
    const arpCount = Math.round(
      ARP_NOTES_MIN + (ARP_NOTES_MAX - ARP_NOTES_MIN) * arpGain,
    );
    for (const step of pickN(ARP_EIGHTH_STEPS, arpCount, rng)) {
      const degree = Math.floor(rng() * ARP_DEGREE_RANGE);
      events.push({
        layer: "arp",
        voice: "arp",
        stepIndex: step,
        freqHz: scaleFreq(degree, ARP_OCTAVE_OFFSET),
        velocity: humanize(rng),
      });
    }
  }

  // L4 — tension: one sustained dissonant note per bar (boss / near-loss
  // only — see `LAYER_ENERGY.tension`'s high threshold).
  if (layerGainForEnergy("tension", e) > 0) {
    events.push({
      layer: "tension",
      voice: "tension",
      stepIndex: 0,
      freqHz: VOICE_DEFS.tension.referenceFreqHz,
      velocity: 1,
    });
  }

  // Lead — the restrained melody (Task-003, 2026-audio-polish, AC-6/AC-7):
  // gated by its OWN energy threshold (`LAYER_ENERGY.lead`) like every other
  // layer, PLUS a seeded per-bar presence roll (the "occasional rest" — even
  // an energy-eligible bar doesn't always play) whose baseline the macro
  // drift nudges (`macroLeadPresenceDelta` — energy and macro multiply, they
  // don't fight). When present, 1–2 notes from the CURRENT phrase's motif
  // (`generatePhraseMotif`, phrase-stable — `phraseIndex = ⌊barIndex /
  // PHRASE_BARS⌋`) place on the half/whole-note grid, rotating through the
  // motif's degrees one bar at a time so the phrase's melody unfolds as bars
  // advance. Appended last — consumes NO rng() draws earlier layers rely on,
  // so this addition is byte-identical-preserving for every other voice
  // (the macro drift above — macroPadVelocity/macroBassJoinThreshold/
  // macroHatFillDelta — does change other voices per bar; see the D-entry,
  // D-302).
  const leadGain = layerGainForEnergy("lead", e);
  if (leadGain > 0) {
    const presence = Math.max(
      0,
      Math.min(1, LEAD_BASE_PRESENCE + macroLeadPresenceDelta(barIndex)),
    );
    if (rng() < presence) {
      const phraseIndex = Math.floor(barIndex / PHRASE_BARS);
      const motif = generatePhraseMotif(seed, phraseIndex);
      const localBar = barIndex % PHRASE_BARS;
      const noteCount = leadGain >= LEAD_DENSE_GAIN ? 2 : 1;
      const slots = pickN(LEAD_SLOT_STEPS, noteCount, rng);
      slots.forEach((step, i) => {
        const degree = motif[(localBar + i) % motif.length]!;
        events.push({
          layer: "lead",
          voice: "lead",
          stepIndex: step,
          freqHz: scaleFreq(degree, LEAD_OCTAVE_OFFSET),
          velocity: humanize(rng),
        });
      });
    }
  }

  return events;
}

// ── Pure-math bar renderer (the headless MUSIC_BED loudness witness) ───────

const DEFAULT_SAMPLE_RATE = 44100;

/** Temporarily substitute `Math.random` with a seeded generator so the
 *  "noise"-type voices (kick/hat) synthesize deterministically too — per the
 *  audio-layer rule (`AUDIO_DIRECTION.md:9`) routed through this module's
 *  PRNG rather than left ambient, matching the precedent already established
 *  by `tests/systems/AudioLoudness.test.ts`'s `synthDeterministic`. Restored
 *  in `finally` — never leaks outside this one synthesis call. */
function synthesizeVoiceSamples(
  profile: ProceduralProfile,
  sampleRate: number,
  rng: () => number,
): Float32Array {
  const original = Math.random;
  Math.random = rng;
  try {
    return synthesizeProceduralSamples(profile, sampleRate);
  } finally {
    Math.random = original;
  }
}

/**
 * Pure-math bar renderer (Task-001, AC-6) — the headless loudness-witness
 * counterpart of the runtime composer: mixes `bars` bars' worth of the SAME
 * voice recipes/pattern/energy-gain math the scheduler plays, at
 * `sampleRate`, into one summed `Float32Array`. No Web Audio anywhere —
 * `tests/systems/AudioLoudness.test.ts` feeds the result through the same
 * RMS × bus-gain math as every other loudness witness. Pure given its
 * arguments (see the module doc's determinism contract).
 */
export function renderBarSamples(
  seed: number,
  energy: number,
  bars: number,
  sampleRate: number = DEFAULT_SAMPLE_RATE,
): Float32Array {
  const e = Math.max(0, Math.min(1, energy));
  const barCount = Math.max(1, Math.floor(bars));
  const stepSamples = Math.max(1, Math.round(secondsPerStep() * sampleRate));
  const barSamples = stepSamples * STEPS_PER_BAR;
  const out = new Float32Array(barSamples * barCount);

  let noteCounter = 0;
  for (let bar = 0; bar < barCount; bar++) {
    const events = generateBarPattern(seed, e, bar);
    for (const ev of events) {
      noteCounter++;
      const def = VOICE_DEFS[ev.voice];
      const profile: ProceduralProfile = def.pitched
        ? { ...def.recipe, freq: ev.freqHz }
        : def.recipe;
      const noteRng = mulberry32(
        barSeed(seed, bar) ^ Math.imul(noteCounter, 0x85ebca6b),
      );
      const samples = synthesizeVoiceSamples(profile, sampleRate, noteRng);
      const layerMul = layerGainForEnergy(ev.layer, e) * ev.velocity;
      if (layerMul <= 0) continue;
      const startSample = bar * barSamples + ev.stepIndex * stepSamples;
      for (let i = 0; i < samples.length; i++) {
        const idx = startSample + i;
        if (idx >= out.length) break;
        out[idx] = (out[idx] ?? 0) + samples[i]! * layerMul;
      }
    }
  }
  return out;
}
